import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import Markdown from "../../components/Markdown";
import { useSnackbar } from "../../components/snackbarProvider";
import { useTenant } from "../../context/TenantContext";
import InteractiveMapCanvas from "../../components/InteractiveMapCanvas";
import useKnowledgeCoverageDashboard from "../../hooks/useKnowledgeCoverageDashboard";

import { graphqlEndpoint } from "../../config";
import { READ_PLAYBOOK } from "../../graph/playbooks/queries";
import { UPDATE_PLAYBOOK_ITEM } from "../../graph/playbooks/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const formatSequence = (value) => {
  const sequence = Number.parseInt(value, 10);
  return Number.isFinite(sequence) ? sequence / 1024 : "—";
};

const sequenceSortValue = (item, originalIndex) => {
  const sequence = Number.parseInt(item.sequence, 10);
  return Number.isFinite(sequence) ? sequence : originalIndex * 1024;
};

const FLOW_NODE_WIDTH = 250;
const FLOW_NODE_HEIGHT = 158;
const FLOW_NODE_GAP = 150;
const FLOW_PADDING = 48;

const wrapLabel = (value, maxCharacters = 16) => {
  const words = String(value || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";

  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length > maxCharacters && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  });

  if (line) lines.push(line);
  return lines;
};

const ViewPlaybook = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, tenantName } = useTenant();
  const { setSnackbar } = useSnackbar();
  const {
    data: knowledgeCoverage,
    loading: coverageLoading,
    error: coverageError,
  } = useKnowledgeCoverageDashboard({ tenantId });

  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const [playbook, setPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(location.state?.viewMode || "map");
  const [selectedItemId, setSelectedItemId] = useState(location.state?.selectedItemId || "");
  const [selectedTransitionId, setSelectedTransitionId] = useState(
    location.state?.selectedTransitionId || ""
  );
  const [zoom, setZoom] = useState(1);

  const coverageBySopId = useMemo(
    () =>
      new Map(
        (knowledgeCoverage.sopRows || []).map((row) => [String(row.id), row])
      ),
    [knowledgeCoverage.sopRows]
  );

  const getSopCoverage = (sopId) => {
    if (coverageLoading) {
      return { label: "Loading coverage", color: "default" };
    }
    if (coverageError) {
      return { label: "Coverage unavailable", color: "default" };
    }

    const row = coverageBySopId.get(String(sopId));
    if (!row) {
      return { label: "No staff assigned", color: "error" };
    }

    const assignedCount = Number(row.assignedCount);
    const understoodCount = Number(row.understoodCount);
    if (
      !Number.isInteger(assignedCount) ||
      !Number.isInteger(understoodCount) ||
      assignedCount < 0 ||
      understoodCount < 0 ||
      understoodCount > assignedCount
    ) {
      return { label: "Coverage unavailable", color: "default" };
    }

    if (assignedCount === 0) {
      return { label: "No staff assigned", color: "error" };
    }

    const color =
      understoodCount === 0
        ? "error"
        : understoodCount === 1
          ? "warning"
          : "success";

    return {
      label: `${understoodCount} of ${assignedCount} understand`,
      color,
    };
  };

  const redirectPath = location.state?.redirectPath || "/managePlaybooks";
  const cameFromBusinessStructure = redirectPath === "/manageBusinessStructure";

  useEffect(() => {
    if (!tenantId || !id) return;

    let active = true;
    const loadPlaybook = async () => {
      setLoading(true);

      try {
        const { readPlaybook } = await client.request(READ_PLAYBOOK, { id });
        if (!active) return;

        if (!readPlaybook) {
          setSnackbar({
            open: true,
            severity: "error",
            message: "Playbook not found",
          });
          navigate(redirectPath);
          return;
        }

        setPlaybook(readPlaybook);
        const requestedItemId = location.state?.selectedItemId;
        const requestedTransitionId = location.state?.selectedTransitionId;
        setViewMode(location.state?.viewMode || "map");
        setSelectedItemId(
          readPlaybook.items?.some((item) => item.id === requestedItemId)
            ? requestedItemId
            : readPlaybook.items?.[0]?.id || ""
        );
        setSelectedTransitionId(
          readPlaybook.transitions?.some((item) => item.id === requestedTransitionId)
            ? requestedTransitionId
            : ""
        );
      } catch (err) {
        if (!active) return;
        console.error("Failed to load playbook:", err);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load playbook",
        });
      } finally {
        if (active) setLoading(false);
      }
    };

    loadPlaybook();
    return () => {
      active = false;
    };
  }, [id, tenantId, navigate, redirectPath, location.key, location.state, setSnackbar]);

  const handleViewSop = (sopId) => {
    if (!sopId) return;
    navigate(`/sops/read/${sopId}`, {
      state: {
        redirectPath: location.pathname,
        returnState: {
          ...location.state,
          viewMode,
          selectedItemId,
          selectedTransitionId,
        },
      },
    });
  };

  const orderedItems = useMemo(() => {
    const items = playbook?.items || [];
    return items
      .map((item, index) => ({ item, index, order: sequenceSortValue(item, index) }))
      .sort((a, b) => a.order - b.order || a.index - b.index)
      .map(({ item }) => item);
  }, [playbook?.items]);

  const transitions = playbook?.transitions || [];

  const flowLayout = useMemo(() => {
    const itemIndexById = new Map(
      orderedItems.map((item, index) => [item.id, index])
    );
    const mapped = transitions
      .map((transition) => ({
        transition,
        fromIndex: itemIndexById.get(
          transition.fromPlaybookItem?.id || transition.fromPlaybookItemId
        ),
        toIndex: itemIndexById.get(
          transition.toPlaybookItem?.id || transition.toPlaybookItemId
        ),
      }))
      .filter(({ fromIndex, toIndex }) => fromIndex !== undefined && toIndex !== undefined);

    const defaultY = FLOW_PADDING + 28;
    const nodes = orderedItems.map((item, index) => ({
      item,
      index,
      x: item.positionX ?? FLOW_PADDING + index * (FLOW_NODE_WIDTH + FLOW_NODE_GAP),
      y: item.positionY ?? defaultY,
    }));
    const nodeById = new Map(nodes.map((node) => [node.item.id, node]));

    const links = mapped.map((entry) => {
      const { transition } = entry;
      const from = nodeById.get(
        transition.fromPlaybookItem?.id || transition.fromPlaybookItemId
      );
      const to = nodeById.get(
        transition.toPlaybookItem?.id || transition.toPlaybookItemId
      );
      if (!from || !to) return null;

      const fromCenterX = from.x + FLOW_NODE_WIDTH / 2;
      const fromCenterY = from.y + FLOW_NODE_HEIGHT / 2;
      const toCenterX = to.x + FLOW_NODE_WIDTH / 2;
      const toCenterY = to.y + FLOW_NODE_HEIGHT / 2;
      const deltaX = toCenterX - fromCenterX;
      const deltaY = toCenterY - fromCenterY;
      const distance = Math.hypot(deltaX, deltaY);
      if (!distance) return null;

      const directionX = deltaX / distance;
      const directionY = deltaY / distance;
      const edgeDistance = (unitX, unitY) => Math.min(
        unitX === 0 ? Infinity : (FLOW_NODE_WIDTH / 2) / Math.abs(unitX),
        unitY === 0 ? Infinity : (FLOW_NODE_HEIGHT / 2) / Math.abs(unitY)
      );
      const fromEdge = edgeDistance(directionX, directionY) + 3;
      const toEdge = edgeDistance(directionX, directionY) + 12;
      const startX = fromCenterX + directionX * fromEdge;
      const startY = fromCenterY + directionY * fromEdge;
      const endX = toCenterX - directionX * toEdge;
      const endY = toCenterY - directionY * toEdge;
      return {
        ...entry,
        path: `M ${startX} ${startY} L ${endX} ${endY}`,
        labelX: (startX + endX) / 2,
        labelY: (startY + endY) / 2,
        labelMaxWidth: 240,
      };
    }).filter(Boolean);

    const width = Math.max(1100, ...nodes.map(({ x }) => x + FLOW_NODE_WIDTH + 80));
    const height = Math.max(650, ...nodes.map(({ y }) => y + FLOW_NODE_HEIGHT + 80));
    return { nodes, links, width, height };
  }, [orderedItems, transitions]);

  const updateItemPosition = (itemId, positionX, positionY) => {
    setPlaybook((current) => current && ({
      ...current,
      items: current.items.map((item) =>
        item.id === itemId ? { ...item, positionX, positionY } : item
      ),
    }));
  };

  const startDrag = (event, item, position) => {
    if (event.target.closest("button")) return;
    event.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    const pointerWorldX =
      ((event.clientX - (rect?.left || 0)) + (canvasRef.current?.scrollLeft || 0)) / zoom;
    const pointerWorldY =
      ((event.clientY - (rect?.top || 0)) + (canvasRef.current?.scrollTop || 0)) / zoom;
    dragRef.current = {
      itemId: item.id,
      pointerId: event.pointerId,
      startX: position.x,
      startY: position.y,
      lastX: position.x,
      lastY: position.y,
      offsetX: pointerWorldX - position.x,
      offsetY: pointerWorldY - position.y,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(16, (event.clientX - rect.left + canvasRef.current.scrollLeft) / zoom - drag.offsetX);
    const y = Math.max(16, (event.clientY - rect.top + canvasRef.current.scrollTop) / zoom - drag.offsetY);
    drag.lastX = x;
    drag.lastY = y;
    updateItemPosition(drag.itemId, x, y);
  };

  const endDrag = async () => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    try {
      await client.request(UPDATE_PLAYBOOK_ITEM, {
        input: { id: drag.itemId, positionX: drag.lastX, positionY: drag.lastY },
      });
    } catch (error) {
      console.error("Failed to save playbook map item position:", error);
      updateItemPosition(drag.itemId, drag.startX, drag.startY);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Could not save the procedure position",
      });
    }
  };

  const selectedItem = orderedItems.find((item) => item.id === selectedItemId) || null;
  const selectedTransition = transitions.find((item) => item.id === selectedTransitionId) || null;
  const selectedTransitionFrom = selectedTransition
    ? orderedItems.find(
        (item) =>
          item.id ===
          (selectedTransition.fromPlaybookItem?.id || selectedTransition.fromPlaybookItemId)
      )
    : null;
  const selectedTransitionTo = selectedTransition
    ? orderedItems.find(
        (item) =>
          item.id ===
          (selectedTransition.toPlaybookItem?.id || selectedTransition.toPlaybookItemId)
      )
    : null;

  const itemColumns = useMemo(
    () => [
      {
        field: "sequence",
        headerName: "Seq",
        flex: 0.3,
        renderCell: ({ value }) => formatSequence(value),
      },
      {
        field: "sopTitle",
        headerName: "SOP",
        flex: 1.8,
        renderCell: ({ row }) => row.sop?.title || "Unknown SOP",
      },
      {
        field: "actions",
        headerName: "View",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Tooltip title="Read SOP">
            <IconButton
              aria-label={`Read ${row.sop?.title || "SOP"}`}
              onClick={() => handleViewSop(row.sopId)}
            >
              <VisibilityOutlinedIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [location.pathname]
  );

  const transitionColumns = [
    {
      field: "from",
      headerName: "From",
      flex: 1.5,
      renderCell: ({ row }) =>
        `${formatSequence(row.fromPlaybookItem?.sequence)}. ${row.fromPlaybookItem?.sop?.title || "Unknown SOP"}`,
    },
    {
      field: "to",
      headerName: "To",
      flex: 1.5,
      renderCell: ({ row }) =>
        `${formatSequence(row.toPlaybookItem?.sequence)}. ${row.toPlaybookItem?.sop?.title || "Unknown SOP"}`,
    },
    {
      field: "outcomeKey",
      headerName: "Outcome",
      flex: 1,
      renderCell: ({ value }) => value || "—",
    },
    {
      field: "label",
      headerName: "Label",
      flex: 1.2,
      renderCell: ({ value }) => value || "—",
    },
    {
      field: "isDefault",
      headerName: "Default",
      flex: 0.7,
      renderCell: ({ value }) => (value ? "Yes" : "No"),
    },
  ];

  const handleMapLinkClick = (transitionId) => {
    setSelectedTransitionId(transitionId);
    setSelectedItemId("");
  };

  if (!tenantId) return <Typography>Select tenant first</Typography>;
  if (loading) return <Typography>Loading playbook...</Typography>;
  if (!playbook) return null;

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        mb={3}
      >
        <Header
          title="VIEW PLAYBOOK"
          subtitle={`Playbook for ${tenantName || "your tenant"}`}
        />
        <ActionButton tone="teal" onClick={() => navigate(redirectPath)}>
          {cameFromBusinessStructure ? "Back to Business Structure" : "Back to Playbooks"}
        </ActionButton>
      </Box>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h3" mb={1}>
          {playbook.name}
        </Typography>
        <Typography variant="overline" color="text.secondary">
          Objective
        </Typography>
        <Typography sx={{ mb: 3 }}>
          {playbook.objective || "No objective provided"}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {playbook.description?.trim() ? (
          <Markdown>{playbook.description}</Markdown>
        ) : (
          <Typography color="text.secondary">No description provided.</Typography>
        )}
      </Paper>

      <Box mt={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
          mb={2}
        >
          <Header
            title="PLAYBOOK FLOW"
            subtitle="Explore how procedures and outcomes connect"
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_, value) => value && setViewMode(value)}
            aria-label="Playbook flow display"
          >
            <ToggleButton value="map">Map</ToggleButton>
            <ToggleButton value="tables">Tables</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {viewMode === "map" ? (
          orderedItems.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography color="text.secondary">This playbook has no procedures yet.</Typography>
            </Paper>
          ) : (
            <>
              <InteractiveMapCanvas
                ref={canvasRef}
                width={flowLayout.width}
                height={flowLayout.height}
                zoom={zoom}
                onZoomChange={setZoom}
                onPointerMove={moveDrag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                description="Drag cards to arrange the flow. Status shows assigned staff who understand every task in the current SOP version: 0 red, 1 amber, 2+ green."
                itemCount={orderedItems.length}
                itemLabel="procedure"
                connectionCount={transitions.length}
                connectionLabel="transition"
              >
                  <svg
                    width={flowLayout.width}
                    height={flowLayout.height}
                    style={{ position: "absolute", inset: 0, overflow: "visible", pointerEvents: "none" }}
                  >
                    <defs>
                      <marker
                        id="playbook-flow-arrow"
                        viewBox="0 0 10 10"
                        markerWidth="12"
                        markerHeight="12"
                        refX="9"
                        refY="5"
                        orient="auto"
                        markerUnits="userSpaceOnUse"
                      >
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#4aa6a1" />
                      </marker>
                    </defs>
                    {flowLayout.links.map(({ transition, path, labelX, labelY, labelMaxWidth }) => {
                      const label = transition.label || transition.outcomeKey || "Continue";
                      const lines = wrapLabel(label);
                      const labelWidth = Math.min(
                        Math.max(90, ...lines.map((line) => line.length * 7 + 18)),
                        labelMaxWidth
                      );
                      const labelHeight = lines.length * 16 + 10;
                      const isSelected = selectedTransitionId === transition.id;

                      return (
                        <g key={transition.id}>
                          <path
                            d={path}
                            fill="none"
                            stroke={isSelected ? "#f3c969" : "#4aa6a1"}
                            strokeWidth={isSelected ? 3.5 : 2.5}
                            markerEnd="url(#playbook-flow-arrow)"
                          />
                          <g
                            pointerEvents="all"
                            onClick={() => handleMapLinkClick(transition.id)}
                            style={{ cursor: "pointer" }}
                          >
                            <rect
                              x={labelX - labelWidth / 2}
                              y={labelY - labelHeight / 2}
                              width={labelWidth}
                              height={labelHeight}
                              rx="6"
                              fill="#52565a"
                              stroke={isSelected ? "#f3c969" : "rgba(255,255,255,0.25)"}
                              strokeWidth={isSelected ? 2 : 1}
                            />
                            <text
                              x={labelX}
                              y={labelY - ((lines.length - 1) * 16) / 2 + 4}
                              textAnchor="middle"
                              fill="#ffffff"
                              fontSize="11"
                              fontWeight="600"
                            >
                              {lines.map((line, index) => (
                                <tspan key={`${transition.id}-${index}`} x={labelX} dy={index === 0 ? 0 : 16}>
                                  {line}
                                </tspan>
                              ))}
                            </text>
                          </g>
                        </g>
                      );
                    })}
                  </svg>

                  {flowLayout.nodes.map(({ item, index, x, y }) => {
                    const isSelected = selectedItemId === item.id;
                    const coverage = getSopCoverage(item.sopId);
                    return (
                      <Paper
                        key={item.id}
                        variant="outlined"
                        onPointerDown={(event) => startDrag(event, item, { x, y })}
                        onClick={() => {
                          setSelectedItemId(item.id);
                          setSelectedTransitionId("");
                        }}
                        sx={{
                          position: "absolute",
                          left: x,
                          top: y,
                          width: FLOW_NODE_WIDTH,
                          height: FLOW_NODE_HEIGHT,
                          p: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          cursor: "grab",
                          touchAction: "none",
                          userSelect: "none",
                          bgcolor: isSelected ? "action.selected" : "background.paper",
                          borderColor: isSelected ? "secondary.main" : "divider",
                          borderWidth: isSelected ? 2 : 1,
                          boxShadow: 3,
                        }}
                      >
                        <Box>
                          <Box sx={{ mb: 1 }}>
                            <Chip
                              size="small"
                              color="secondary"
                              label={`Step ${formatSequence(item.sequence) === "—" ? index + 1 : formatSequence(item.sequence)}`}
                            />
                          </Box>
                          <Typography
                            fontWeight={700}
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {item.sop?.title || "Unknown SOP"}
                          </Typography>
                        </Box>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Chip
                            size="small"
                            color={coverage.color}
                            label={coverage.label}
                            sx={{ maxWidth: "60%" }}
                          />
                          <Button
                            size="small"
                            startIcon={<VisibilityOutlinedIcon />}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleViewSop(item.sopId);
                            }}
                            sx={{ minWidth: 0, px: 1, whiteSpace: "nowrap" }}
                          >
                            View SOP
                          </Button>
                        </Stack>
                      </Paper>
                    );
                  })}
              </InteractiveMapCanvas>

              {(selectedItem || selectedTransition) && (
                <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                  {selectedItem && (
                    <>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                        <Box>
                          <Typography variant="h6" fontWeight={700}>
                            {formatSequence(selectedItem.sequence)}. {selectedItem.sop?.title || "Unknown SOP"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Select a procedure to see its handoff criteria.
                          </Typography>
                        </Box>
                        <Button onClick={() => handleViewSop(selectedItem.sopId)} startIcon={<VisibilityOutlinedIcon />}>
                          Read SOP
                        </Button>
                      </Stack>
                      {(selectedItem.entryCriteria || selectedItem.exitCriteria) && (
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mt={2}>
                          {selectedItem.entryCriteria && (
                            <Box flex={1}>
                              <Typography variant="overline" color="text.secondary">Entry criteria</Typography>
                              <Typography variant="body2">{selectedItem.entryCriteria}</Typography>
                            </Box>
                          )}
                          {selectedItem.exitCriteria && (
                            <Box flex={1}>
                              <Typography variant="overline" color="text.secondary">Exit criteria</Typography>
                              <Typography variant="body2">{selectedItem.exitCriteria}</Typography>
                            </Box>
                          )}
                        </Stack>
                      )}
                    </>
                  )}
                  {selectedTransition && (
                    <Box>
                      <Typography variant="h6" fontWeight={700} mb={1}>Transition details</Typography>
                      <Typography variant="body2">
                        {selectedTransitionFrom?.sop?.title || "Unknown SOP"} → {selectedTransitionTo?.sop?.title || "Unknown SOP"}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                        {selectedTransition.label && <Chip size="small" label={selectedTransition.label} />}
                        {selectedTransition.outcomeKey && <Chip size="small" variant="outlined" label={`Outcome: ${selectedTransition.outcomeKey}`} />}
                        <Chip size="small" color={selectedTransition.isDefault ? "success" : "default"} label={selectedTransition.isDefault ? "Default path" : "Alternative path"} />
                      </Stack>
                    </Box>
                  )}
                </Paper>
              )}
            </>
          )
        ) : (
          <>
            <Header title="PLAYBOOK ITEMS" subtitle="Follow the SOPs in sequence" />
            <DataGrid
              autoHeight
              rows={orderedItems}
              columns={itemColumns}
              disableRowSelectionOnClick
              hideFooter
            />

            {transitions.length > 0 && (
              <Box mt={4}>
                <Header title="TRANSITIONS" subtitle="How work moves between playbook items" />
                <DataGrid
                  autoHeight
                  rows={transitions}
                  columns={transitionColumns}
                  disableRowSelectionOnClick
                  hideFooter
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ViewPlaybook;
